import { forwardRef, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
// `motion` is used as <motion.div>; the base no-unused-vars rule does not see JSX member tags.
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Heart, Calendar, Stethoscope, Users, UserCircle, CalendarDays, BarChart3, Landmark, UserRoundPen, MessageCircle, HelpCircle, Mail, CheckCircle2, Check, BadgeCheck, CreditCard, Settings2, Pill, ShieldCheck, UserCog, Building2, LifeBuoy, UsersRound, TestTube, Scan, HeartHandshake,
  Gauge, ListChecks, Contact, ScanBarcode, Boxes, AlertTriangle, Receipt, Truck, ClipboardList, PackageCheck, Undo2, FlaskConical, Syringe, FileText, ScanLine, FileImage, Radiation, Cpu, PieChart, LineChart, TrendingUp, Activity, FileBarChart, Sparkles, MessagesSquare, UserPlus, Wallet, ReceiptText, Layers, CalendarClock, Share2, ClipboardCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HeartLoader from '../HeartLoader';
import CollapsibleSidebar from './TwoLevelSidebar';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../../redux/blockingSlice';
import { getPatientById } from '../../redux/patientSlice';
import { getDoctorById } from '../../redux/doctorSlice';
import { getAdminById } from '../../redux/adminSlice';
import { getMyStaffProfile } from '../../redux/staffSlice';
import { canUse } from '../../redux/subscriptionSlice';
import { planAllows } from '../../hooks/usePlanControl';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import usePermissions from '../../hooks/usePermissions';
import { getDoctorSetupStatus } from '../../utils/doctorSetupStatus';
import logo from '../../assets/img/logo-TT2.png';
import getFileURL from '../../utils/fileURL';

// PER-ITEM ICONS for the single-column sidebar. The product owner's ask:
// "many options reuse the same icon; use a different icon for each option." Each
// nav item id maps to its OWN meaningful lucide icon, and no icon repeats inside
// any one role's menu (an id appears in at most one role at a time, so the same
// icon may be reused across roles). Adding a nav item? Give it a distinct icon
// here — an id with no entry simply renders without one.
const NAV_ICON = {
  // Dashboards
  'nav-admin-dashboard': LayoutDashboard,
  'nav-dashboard': LayoutDashboard,
  'nav-doctor-dashboard': Gauge,
  // Consult / doctor
  'nav-appointment-calendar': Calendar,
  'nav-appointment-list': ListChecks,
  'nav-doctor': Stethoscope,
  'nav-my-patient': Contact,
  'nav-shared-visits': Share2,
  'nav-medicines': Pill,
  'nav-patient-chat': MessageCircle,
  // Pharma
  'nav-pharmacy-counter': ScanBarcode,
  'nav-pharmacy-medicines': Pill,
  'nav-pharmacy-racks': Boxes,
  'nav-pharmacy-stock': AlertTriangle,
  'nav-pharmacy-bills': Receipt,
  'nav-pharmacy-suppliers': Truck,
  'nav-pharmacy-po': ClipboardList,
  'nav-pharmacy-grn': PackageCheck,
  'nav-pharmacy-returns': Undo2,
  // Lab
  'nav-lab-worklist': FlaskConical,
  'nav-lab-collection': Syringe,
  'nav-lab-reports': FileText,
  'nav-lab-tests': TestTube,
  // Radiology
  'nav-rad-worklist': ScanLine,
  'nav-rad-reporting': FileImage,
  'nav-rad-procedures': Radiation,
  'nav-rad-equipment': Cpu,
  // Caretaker
  'nav-care-visits': HeartHandshake,
  // Patients
  'nav-patient': Users,
  // Reports
  'nav-reports-doctors': BarChart3,
  'nav-reports-patients': PieChart,
  'nav-reports-doctor-count': LineChart,
  'nav-reports-patient-count': TrendingUp,
  'nav-reports-doctor-status': Activity,
  'nav-reports-services': FileBarChart,
  'nav-ai-reports': Sparkles,
  // Billing
  'nav-service-billing': ReceiptText,
  // Communication
  'nav-team-chat': MessagesSquare,
  'nav-support': LifeBuoy,
  // Users
  'nav-users-all': UsersRound,
  'nav-user-roles': ShieldCheck,
  // Clinical setup
  // Short labels on purpose (owner, 1 Oct 2026): the section is already
  // "Clinical setup", and the orgWords rewrite turned "Hospital …" into the
  // tenant's full name on the open platform.
  'nav-check-sets': ClipboardCheck,
  'nav-programmes': CalendarClock,
  'nav-clicktogo': UserPlus,
  // Subscription
  'nav-subscription-packages': CreditCard,
  'nav-subscription-payments': Wallet,
  'nav-subscription-sub-items': Layers,
  // Platform
  'nav-doctor-review': BadgeCheck,
  // Account
  'nav-admin-profile': UserRoundPen,
  'nav-doctor-profile': UserRoundPen,
  'nav-staff-profile': UserRoundPen,
  'nav-patient-profile': UserRoundPen,
  'nav-settings': Settings2,
  'nav-doctor-settings': Settings2,
  // Doctor practice
  'nav-payment': Landmark,
  'nav-my-subscription': CreditCard,
  // Patient extras
  'nav-patient-appointment': CalendarClock,
  'nav-my-appointment': CalendarDays,
  'nav-my-doctor': Stethoscope,
};

const Sidebar = forwardRef(({ isOpen, onClose }, ref) => {
  const { t } = useTranslation('sidebar');
  const { selectedPatient } = useSelector((state) => state.patient);
  const { selectedDoctor, isLoading } = useSelector((state) => state.doctor);
  const { admin } = useSelector((state) => state.admin);
  // Tenant staff: their own profile row, for the name + avatar in the rail.
  const myStaff = useSelector((state) => state.staff?.myProfile);
  const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
  // Admin-controlled feature flags. These gate the role-specific sub-items
  // below (Patient Library for doctors, Doctor Library for patients).
  // Patient Library is now gated by the doctor's subscription package AND the
  // Doctor–patient chat is gated by the doctor's subscription plan.
  const doctorChatEnabled = useSelector((state) => canUse(state, 'doctor_patient_chat'));
  // 1 Oct 2026 (owner): every plan feature is refused by the server now, so
  // the doctor's rail drops what their plan lacks — "Shared with me" without
  // visit_sharing, AI Reports without the ai_reports control (the hospital's
  // ai_reports switch still applies on top, through feat()).
  // planAllows: hidden only once the resolved access says the plan rule
  // governs this doctor (see hooks/usePlanControl.js).
  const visitSharingPlanOn = useSelector((state) => planAllows(state, 'visit_sharing', 'doctor'));
  const aiReportsPlanOn = useSelector((state) => planAllows(state, 'ai_reports', 'doctor'));
  // Visits shared while the doctor was elsewhere (VisitShareSocketBridge
  // counts them; opening Shared with me clears it). Badge on that row.
  const unseenShares = useSelector((state) => state.appointmentShare?.unseenCount || 0);
  const location = useLocation();
  const userRole = localStorage.getItem('Role') || 'patient';
  const doctorId = localStorage.getItem('ID');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleProfileNavigation = (tabKey) => {
    const state = { tab: tabKey };
    const path = userRole === 'doctor'
      ? '/doctorProfile'
      : userRole === 'patient'
        ? '/patientProfile'
        : userRole === 'admin'
          ? '/adminProfile'
          : userRole === 'user'
            ? '/staffProfile'
            : null;
    if (!path) return;
    if (resnavigate) {
      // Defer the navigation until the dirty tab's discard handler clears the block.
      dispatch(setPendingNavigation({ kind: 'route', value: path, state }));
      dispatch(tryingnavigate(true));
      return;
    }
    navigate(path, { state });
    if (window.innerWidth <= 1024 && typeof onClose === 'function') {
      onClose();
    }
  };

  // Dropdown states
  const [dashboardDropdownOpen, setDashboardDropdownOpen] = useState(false);
  const [appointmentDropdownOpen, setAppointmentDropdownOpen] = useState(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [masterDropdownOpen, setMasterDropdownOpen] = useState(false);
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);
  const [doctorDropdownOpen, setDoctorDropdownOpen] = useState(false);
  const [subscriptionDropdownOpen, setSubscriptionDropdownOpen] = useState(false);
  const [auditDropdownOpen, setAuditDropdownOpen] = useState(false);
  // GENERIC dropdown state, keyed by the nav item's own id.
  //
  // The eight useState flags above are matched to menus by a hard-coded
  // `item.id === 'nav-x'` chain in renderNavItem and a matching if/else chain in
  // handleDropdownClick. A menu added without editing BOTH chains gets
  // dropdownType '', which silently means "not a dropdown": the sub-items can
  // never open and the only symptom is a parent that navigates and appears to
  // have nothing under it. That is exactly what happened to Users and Pharmacy —
  // both shipped with sub-items nobody could reach.
  //
  // New menus use this map instead and need no wiring at all.
  const [openDropdowns, setOpenDropdowns] = useState({});
  const toggleDropdown = (id) =>
    setOpenDropdowns((prev) => ({ ...prev, [id]: !prev[id] }));
  const [imageError, setImageError] = useState(false);

  // Reset image error
  useEffect(() => {
    setImageError(false);
  }, [selectedDoctor, selectedPatient, admin]);

  // Auto-open correct dropdown based on current path.
  //
  // CLASSIC (FLAT) SIDEBAR ONLY. admin / doctor / staff / patient render
  // CollapsibleSidebar, whose sections all start open and which highlights
  // the active row by matching location.pathname against the item data it
  // was handed — so regrouping an item in twoLevelGroups (Users moving under
  // Account, the doctor reports moving under Doctors, …) needs nothing here,
  // and adding a path here would not open anything in that column. These
  // enumerated arrays exist for the one menu still drawn flat — super_admin's,
  // whose tenantOnly() entries are all gone, leaving nothing with a dropdown —
  // and they still match the flat lists they belong to: 'nav-users' below is
  // the flat admin list's Users dropdown (All users / Roles), which keeps that
  // id even though the column files those two rows under Account.
  useEffect(() => {
    const path = location.pathname;

    // The generic menus, opened by prefix rather than by an enumerated list so a
    // new pharmacy screen doesn't need remembering here too. Sticky: once opened
    // it stays open, matching how the legacy menus behave when you navigate
    // between their sub-items.
    setOpenDropdowns((prev) => ({
      ...prev,
      'nav-users': prev['nav-users'] || path === '/users' || path === '/user-roles',
      'nav-pharmacy': prev['nav-pharmacy'] || path.startsWith('/pharmacy'),
      'nav-lab': prev['nav-lab'] || path.startsWith('/lab'),
      'nav-radiology': prev['nav-radiology'] || path.startsWith('/radiology'),
    }));

    if (userRole === 'admin') {
      const isDashboardActive = ['/adminDashboard', '/doctorDashboard'].includes(path);
      setDashboardDropdownOpen(isDashboardActive);

      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);

      const isReportsActive = ['/doctors', '/patients', '/count/doctors', '/count/patients', '/doctor-status'].includes(path);
      setReportsDropdownOpen(isReportsActive);

      const isMasterActive = ['/speciality-category', '/speciality', '/master-support', '/translation', '/country', '/profession', '/catalog-proposals'].includes(path)
        || path.startsWith('/master-catalog/');
      setMasterDropdownOpen(isMasterActive);

      const isSubscriptionActive = ['/sub-items', '/subscription', '/subscriptionPayments'].includes(path);
      setSubscriptionDropdownOpen(isSubscriptionActive);
      const isAuditActive = ['/audit/api', '/audit/socket', '/audit/login', '/audit/activity', '/audit/appointments', '/audit/security', '/audit/ai'].includes(path);
      setAuditDropdownOpen(isAuditActive);
    } else if (userRole === 'doctor') {
      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);

      const isPatientActive = ['/myPatient', '/patient', '/patientProfile'].includes(path);
      setPatientDropdownOpen(isPatientActive);
    } else if (userRole === 'patient') {
      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);

      const isDoctorActive = ['/myDoctor', '/doctor', '/doctorProfile'].includes(path);
      setDoctorDropdownOpen(isDoctorActive);
    } else {
      const isAppointmentActive = ['/appointment', '/appointmentList'].includes(path);
      setAppointmentDropdownOpen(isAppointmentActive);
    }
  }, [location.pathname, userRole]);

  // Load user profile
  useEffect(() => {
    if (!selectedPatient && userRole === 'patient' && doctorId) {
      dispatch(getPatientById(doctorId));
    } else if (!selectedDoctor && userRole === 'doctor' && doctorId) {
      dispatch(getDoctorById(doctorId));
    } else if (!admin && userRole === 'admin' && doctorId) {
      dispatch(getAdminById(doctorId));
    } else if (!myStaff && userRole === 'user') {
      // Keyed on the token, not on an id from storage — one employee must not be
      // able to load another's profile by editing localStorage.
      dispatch(getMyStaffProfile());
    }
  }, [userRole, doctorId, dispatch, selectedPatient, selectedDoctor, admin, myStaff]);

  // User info
  const userName =
    userRole === 'doctor'
      ? selectedDoctor?.name || t('sidebar.doctors')
      : userRole === 'patient'
        ? selectedPatient?.name || t('sidebar.patients')
        : userRole === 'admin'
          ? admin?.name || t('sidebar.admins')
          : userRole === 'user'
            ? myStaff?.name || t('sidebar.staff', 'Staff')
            : t('sidebar.guest');

  const user = {
    name: userName,
    image:
      userRole === 'doctor' ? selectedDoctor?.image || ''
        : userRole === 'admin' ? admin?.image || ''
          : userRole === 'patient' ? selectedPatient?.image || ''
            : userRole === 'user' ? myStaff?.image || ''
              : '',
  };

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    if (userRole === 'patient' && selectedPatient) {
      const fields = [
        'name', 'surname', 'gender', 'dob', 'email', 'phone',
        'address', 'country', 'city', 'zipCode', 'occupation', 'image'
      ];
      const filledFields = fields.filter(field => {
        const value = selectedPatient[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });
      return Math.round((filledFields.length / fields.length) * 100);
    } else if (userRole === 'doctor' && selectedDoctor) {
      const fields = [
        'name', 'surname', 'gender', 'dob', 'email', 'phone',
        'licenseNumber', 'description', 'image'
      ];
      const filledFields = fields.filter(field => {
        const value = selectedDoctor[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });

      // Check websiteLink array - must have at least 1 item
      const hasWebsiteLink = selectedDoctor.websiteLink &&
        Array.isArray(selectedDoctor.websiteLink) &&
        selectedDoctor.websiteLink.length >= 1;

      // Check certification array - must have at least 1 item
      const hasCertification = selectedDoctor.certification &&
        Array.isArray(selectedDoctor.certification) &&
        selectedDoctor.certification.length >= 1;

      // Total fields = regular fields (9) + websiteLink (1) + certification (1) = 11
      const totalFields = fields.length + 2;
      const completedFields = filledFields.length + (hasWebsiteLink ? 1 : 0) + (hasCertification ? 1 : 0);

      return Math.round((completedFields / totalFields) * 100);
    } else if (userRole === 'admin' && admin) {
      const fields = ['name', 'email', 'image'];
      const filledFields = fields.filter(field => {
        const value = admin[field];
        return value !== null && value !== undefined && value !== '' &&
          (typeof value === 'string' ? value.trim() !== '' : true);
      });
      return Math.round((filledFields.length / fields.length) * 100);
    }
    return 0;
  };

  const profileCompletion = calculateProfileCompletion();

  // Get progress bar color based on completion percentage
  const getProgressColor = (percentage) => {
    if (percentage < 34) {
      // Red for low completion (0-33%)
      return 'bg-gradient-to-r from-red-500 to-red-600';
    } else if (percentage < 67) {
      // Yellow/Orange for medium completion (34-66%)
      return 'bg-gradient-to-r from-yellow-500 to-orange-500';
    } else {
      // Green for high completion (67-100%)
      return 'bg-gradient-to-r from-emerald-400 to-emerald-600';
    }
  };

  // Get percentage text color based on completion
  const getPercentageTextColor = (percentage) => {
    if (percentage < 34) {
      return 'text-red-600';
    } else if (percentage < 67) {
      return 'text-orange-500';
    } else {
      return 'text-green-600';
    }
  };

  // Get doctor setup status using utility function
  const setupStatus = getDoctorSetupStatus(userRole, selectedDoctor);

  const isProfile100 = profileCompletion === 100;
  const isSetup100 = setupStatus ? (setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4 && setupStatus.point5) : (userRole !== 'doctor');
  const showBadge = userRole === 'doctor' ? (isSetup100) : (isProfile100 && isSetup100);


  // super_admin runs the platform: Tenants, Admins, Platform Support, Translation
  // and Audit & Security are theirs alone. Everything operational inside one
  // hospital — including Subscription — belongs to the tenant admin. (Role is
  // normalised to 'admin' for both; this flag is the only client-side
  // distinction.)
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  // PER-HOSPITAL FEATURES. Hides what this hospital hasn't got. Hiding is the
  // polite half only — the API refuses the same features via
  // requireTenantFeature, so a hidden menu is not the control.
  //
  // Fails OPEN (see the hook): if the lookup fails we show everything rather
  // than presenting an empty sidebar, because the server still enforces it.
  const { has: hasFeature, isOpenPlatform } = useTenantFeatures();

  // WHAT THIS PERSON MAY DO. Only meaningful for staff — `can()` returns true
  // for everyone else, so the doctor and admin rails below are unaffected by it.
  // Hiding a menu is the polite half only: every route behind these links is
  // enforced server-side by requirePermission.
  const { can, roleName: staffRoleName } = usePermissions();

  // Drop menu entries whose feature is off for this hospital. Core features are
  // always on, so entries backed by them are never removed.
  const feat = (featureId, ...items) => (hasFeature(featureId) ? items : []);
  // Service reports has a tab for consultations and for pharmacy, lab,
  // radiology and home care (pages/reports/ServiceReports.jsx TABS); the
  // entry needs one of them as well as `reports`, so it never opens on a page
  // that can only be empty (owner, 20 Oct 2026).
  const hasServiceReports = ['appointments', 'pharmacy', 'lab', 'radiology', 'care_visits'].some(hasFeature);

  // Admin Menu Items
  // Super Admin runs the PLATFORM; a tenant admin runs one hospital. These
  // sections are that hospital's day-to-day work (its schedule, its rosters, its
  // billing) and mean nothing to someone who owns every tenant — so they are
  // hidden from Super Admin rather than shown with cross-tenant data mixed in.
  //
  // Super Admin reaches a tenant's own screens through that tenant's
  // maintenance account instead (Tenants -> Maintenance), which keeps the access
  // deliberate and auditable.
  const tenantOnly = (...items) => (isSuperAdmin ? [] : items);

  // PHARMACY. Defined once and reused by both the admin rail and the staff rail —
  // two hand-maintained copies of the same menu is how one of them ends up
  // missing a screen nobody notices for a month.
  //
  // `can()` returns true for admins (usePermissions gives them allowAll), so the
  // same expression correctly shows an admin everything and shows a pharmacist
  // only the parts their sub-role grants.
  const pharmacySubItems = [
    ...(can('pharmacy.dispense') ? [{ name: t('sidebar.pharmacy_counter', 'Counter'), path: '/pharmacy/counter', id: 'nav-pharmacy-counter' }] : []),
    ...(can('pharmacy.stock.manage') ? [{ name: t('sidebar.pharmacy_medicines', 'Medicines'), path: '/pharmacy/medicines', id: 'nav-pharmacy-medicines' }] : []),
    ...(can('pharmacy.location.manage') ? [{ name: t('sidebar.pharmacy_racks', 'Racks & bins'), path: '/pharmacy/locations', id: 'nav-pharmacy-racks' }] : []),
    ...(can('pharmacy.stock.manage') ? [{ name: t('sidebar.pharmacy_stock', 'Stock alerts'), path: '/pharmacy/stock', id: 'nav-pharmacy-stock' }] : []),
    ...(can('pharmacy.bill.reprint') ? [{ name: t('sidebar.pharmacy_bills', 'Bills'), path: '/pharmacy/bills', id: 'nav-pharmacy-bills' }] : []),
    // Purchasing, behind its own tenant feature: a clinic that only dispenses
    // never buys from a distributor and should not carry the screens.
    ...(hasFeature('pharmacy_inventory') && can('pharmacy.purchase.manage')
      ? [{ name: t('sidebar.pharmacy_suppliers', 'Suppliers'), path: '/pharmacy/suppliers', id: 'nav-pharmacy-suppliers' },
         { name: t('sidebar.pharmacy_po', 'Purchase orders'), path: '/pharmacy/purchase-orders', id: 'nav-pharmacy-po' },
         { name: t('sidebar.pharmacy_grn', 'Goods receipt'), path: '/pharmacy/goods-receipt', id: 'nav-pharmacy-grn' }]
      : []),
    ...(hasFeature('pharmacy_inventory') && (can('pharmacy.return') || can('pharmacy.purchase.manage'))
      ? [{ name: t('sidebar.pharmacy_returns', 'Purchase returns'), path: '/pharmacy/purchase-returns', id: 'nav-pharmacy-returns' }]
      : []),
  ];

  // Hidden entirely when the sub-role grants nothing in it — an empty dropdown is
  // worse than no dropdown.
  const pharmacyNav = (hasFeature('pharmacy') && pharmacySubItems.length)
    ? [{
        name: t('sidebar.pharmacy', 'Pharmacy'),
        icon: Pill,
        path: pharmacySubItems[0].path,
        id: 'nav-pharmacy',
        hasDropdown: true,
        activePaths: ['/pharmacy/counter', '/pharmacy/medicines', '/pharmacy/locations', '/pharmacy/stock', '/pharmacy/bills', '/pharmacy/suppliers', '/pharmacy/purchase-orders', '/pharmacy/goods-receipt', '/pharmacy/purchase-returns'],
        subItems: pharmacySubItems,
      }]
    : [];

  // LABORATORY — same shape and same reasoning as pharmacyNav: defined once,
  // reused by the admin rail and the staff rail, and hidden entirely when the
  // sub-role grants nothing in it.
  const labSubItems = [
    ...(can('lab.order.view') ? [{ name: t('sidebar.lab_worklist', 'Worklist'), path: '/lab/worklist', id: 'nav-lab-worklist' }] : []),
    ...(can('lab.sample.collect') ? [{ name: t('sidebar.lab_collection', 'Collection'), path: '/lab/collection', id: 'nav-lab-collection' }] : []),
    ...(can('lab.report.release') ? [{ name: t('sidebar.lab_reports', 'Reports'), path: '/lab/reports', id: 'nav-lab-reports' }] : []),
    ...(can('lab.master.manage') ? [{ name: t('sidebar.lab_tests', 'Test master'), path: '/lab/tests', id: 'nav-lab-tests' }] : []),
  ];
  const labNav = (hasFeature('lab') && labSubItems.length)
    ? [{
        name: t('sidebar.laboratory', 'Laboratory'),
        icon: TestTube,
        path: labSubItems[0].path,
        id: 'nav-lab',
        hasDropdown: true,
        activePaths: ['/lab/worklist', '/lab/collection', '/lab/reports', '/lab/tests'],
        subItems: labSubItems,
      }]
    : [];

  // RADIOLOGY — same shape and reasoning as pharmacyNav and labNav.
  const radSubItems = [
    ...(can('radiology.order.view') ? [{ name: t('sidebar.rad_worklist', 'Worklist'), path: '/radiology/worklist', id: 'nav-rad-worklist' }] : []),
    ...(can('radiology.report.draft') ? [{ name: t('sidebar.rad_reporting', 'Reporting'), path: '/radiology/reporting', id: 'nav-rad-reporting' }] : []),
    ...(can('radiology.master.manage') ? [{ name: t('sidebar.rad_procedures', 'Procedures'), path: '/radiology/procedures', id: 'nav-rad-procedures' }] : []),
    ...(can('radiology.master.manage') ? [{ name: t('sidebar.rad_equipment', 'Equipment'), path: '/radiology/equipment', id: 'nav-rad-equipment' }] : []),
  ];
  const radNav = (hasFeature('radiology') && radSubItems.length)
    ? [{
        name: t('sidebar.radiology', 'Radiology'),
        icon: Scan,
        path: radSubItems[0].path,
        id: 'nav-radiology',
        hasDropdown: true,
        activePaths: ['/radiology/worklist', '/radiology/reporting', '/radiology/procedures', '/radiology/equipment'],
        subItems: radSubItems,
      }]
    : [];

  //
  // CLASSIC (FLAT) LIST — rendered ONLY for super_admin, for whom every
  // tenantOnly() entry below is stripped and just Settings survives. Tenant
  // admins never see this list: twoLevelGroups below always has at least the
  // ungated My Dashboard row, so flatModel / CollapsibleSidebar wins. The
  // column (twoLevelGroups) is the source of truth for the tenant admin menu
  // — it carries Team chat, Service reports, Service billing, Home visits and
  // Doctor review, and gates Doctor Dashboard / Patients / the consult reports
  // on 'appointments' and Doctor plans on 'subscriptions', none of which this
  // list does. Only the SEQUENCE here was aligned to the approved layout
  // (Dashboard · Appointments · Doctor · Send invite · Doctor plans · Patients
  // · Pharma · Lab · Radiology · Reports · AI Reports · Support · Users ·
  // My Profile · Settings, mirroring Reports → Communication → Account); ids
  // and gates are unchanged from before.
  const adminItems = [
    ...tenantOnly(
      {
        name: t('sidebar.dashboard'),
        icon: LayoutDashboard,
        path: '/adminDashboard',
        id: 'nav-dashboard',
        hasDropdown: true,
        subItems: [
          { name: t('sidebar.my_dashboard'), path: '/adminDashboard', id: 'nav-admin-dashboard' },
          { name: t('sidebar.doctor_dashboard'), path: '/doctorDashboard', id: 'nav-doctor-dashboard' },
        ]
      },
      // Appointments and Doctor management belong to the Medi-Consult SERVICE
      // (services restructure): a pharmacy-only or lab-only tenant must not see
      // them. The backend gates my-features by services, so feat() is enough.
      ...feat('appointments', {
        name: t('sidebar.appointment'),
        icon: Calendar,
        path: '/appointment',
        id: 'nav-appointment',
        hasDropdown: true,
        subItems: [
          { name: t('sidebar.calendar'), path: '/appointment', id: 'nav-appointment-calendar' },
          { name: t('sidebar.list'), path: '/appointmentList', id: 'nav-appointment-list' }
        ]
      }),
      ...feat('doctors', { name: t('sidebar.doctor'), icon: Stethoscope, path: '/doctor', id: 'nav-doctor', activePaths: ['/doctor', '/doctorProfile'] }),
    ),
    // Doctor plans belong entirely to the tenant admin — it is that hospital's
    // own doctor billing (packages, payments, enquiries) plus Sub-Items, the
    // access-control catalogue those packages draw from. Super Admin sells
    // TENANTS, not doctor seats inside one, so none of this is their screen.
    ...tenantOnly({
      name: t('sidebar.section_doctor_plans', 'Doctor plans'),
      icon: CreditCard,
      path: '/subscription',
      id: 'nav-subscription',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.packages', 'Packages'), path: '/subscription', id: 'nav-subscription-packages' },
        { name: t('sidebar.payments', 'Payments'), path: '/subscriptionPayments', id: 'nav-subscription-payments' },
        { name: t('sidebar.sub_items', 'Sub-Items'), path: '/sub-items', id: 'nav-subscription-sub-items' },
      ]
    }),
    // Patients are GLOBAL core — every service works on patients, so this
    // stays for every tenant type.
    ...tenantOnly({ name: t('sidebar.patient'), icon: Users, path: '/patient', id: 'nav-patient', activePaths: ['/patient', '/patientProfile'] }),
    // Tenants / Admins / Platform Support (super_admin) moved to the dedicated
    // admin portal (Frontend-superadmin-web).
    ...tenantOnly(...pharmacyNav),
    ...tenantOnly(...labNav),
    ...tenantOnly(...radNav),

    // Reports Section - Now Properly Working
    ...tenantOnly({
      name: t('sidebar.reports'),
      icon: BarChart3,
      path: '/doctors', // default route
      id: 'nav-reports',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.reports_doctors'), path: '/doctors', id: 'nav-reports-doctors' },
        { name: t('sidebar.reports_patients'), path: '/patients', id: 'nav-reports-patients' },
        { name: t('sidebar.reports_doctor_status'), path: '/doctor-status', id: 'nav-reports-doctor-status' },
        { name: t('sidebar.reports_doctor_count', 'Doctor Count'), path: '/count/doctors', id: 'nav-reports-doctor-count' },
        { name: t('sidebar.reports_patient_count', 'Patient Count'), path: '/count/patients', id: 'nav-reports-patient-count' },
      ]
    }),
    ...tenantOnly(...feat('ai_reports', { name: t('sidebar.ai_reports', 'AI Reports'), icon: BarChart3, path: '/aiReports', id: 'nav-ai-reports' })),
    // Every tenant login RAISES tickets here; they are answered in the Super
    // Admin app (Tenant Support queue), so there is no inbox on this side.
    // Sits before the Account run (Users · My Profile · Settings), as the
    // column's Communication heading precedes Account.
    ...tenantOnly(...feat('support_tickets', { name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' })),

    // Master Section (global catalogues, super_admin only) moved to the
    // dedicated admin portal (Frontend-superadmin-web). Tenant admins still
    // SEE this data everywhere it matters — the doctor and patient forms,
    // registration and the appointment modal all read the lists to fill their
    // dropdowns. Only managing the catalogue moved.
    // Audit & Security (super_admin only) moved there too.

    // { name: t('sidebar.chat'), icon: MessageCircle, path: '/chat', id: 'nav-chat' },
    // The hospital's own EMPLOYEES — reception, nursing, pharmacy, lab, scan,
    // billing — and the sub-roles that decide what each may do. Account-side
    // (the column files it under Account); per-hospital, so Super Admin never
    // sees it.
    ...tenantOnly(...feat('user_management', {
      name: t('sidebar.users', 'Users'),
      icon: UsersRound,
      path: '/users',
      id: 'nav-users',
      hasDropdown: true,
      activePaths: ['/users', '/user-roles'],
      subItems: [
        { name: t('sidebar.all_users', 'All users'), path: '/users', id: 'nav-users-all' },
        { name: t('sidebar.user_roles', 'Roles'), path: '/user-roles', id: 'nav-user-roles' },
      ],
    })),
    // Send invite lives in the Account run (owner request, Aug 2026): inviting
    // people into the tenant reads as account administration, not doctoring.
    ...tenantOnly(...feat('invitations', { name: t('sidebar.send_invite'), icon: Mail, path: '/clicktogo', id: 'nav-clicktogo' })),
    ...tenantOnly({ name: t('sidebar.myprofile'), icon: UserRoundPen, path: '/adminProfile', id: 'nav-admin-profile' }),
    { name: t('sidebar.settings', 'Settings'), icon: Settings2, path: '/settings', id: 'nav-settings' },
  ];

  // TWO LAYERS, both must allow it:
  //   hasFeature(...)  what the HOSPITAL bought (per-tenant entitlement)
  //   the existing flags below: the platform setting and the DOCTOR's own plan
  // Entitlement is the ceiling — a doctor's plan can never grant something the
  // hospital hasn't got.
  //
  // ORDER mirrors the doctor's collapsible column below: Dashboard · Consult
  // (appointments, patients) · Communication (chat, support) · My practice
  // (medicines, payment, subscription) · Reports · Account.
  // Dead for doctors in practice (the column always renders — see adminItems);
  // the column additionally carries Team chat.
  const doctorItems = [
    { name: t('sidebar.dashboard'), icon: LayoutDashboard, path: '/doctorDashboard', id: 'nav-dashboard' },
    {
      name: t('sidebar.appointment'),
      icon: Calendar,
      path: '/appointment',
      id: 'nav-appointment',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.calendar'), path: '/appointment', id: 'nav-appointment-calendar' },
        { name: t('sidebar.list'), path: '/appointmentList', id: 'nav-appointment-list' }
      ]
    },
    {
      name: t('sidebar.patient'),
      icon: Users,
      path: '/myPatient',
      id: 'nav-patient',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.my_patient'), path: '/myPatient', id: 'nav-my-patient', activePaths: ['/myPatient', '/patientProfile'] },
        // Patient Library removed (decisions log #18): the tenant-scoped
        // Patients page IS the library; the old page browsed ALL patients.
      ]
    },
    // Past visits other hospitals' patients have shared with this doctor.
    // No feature flag: the share is the PATIENT's consent, and a hospital
    // switch has no say over what its doctor was personally trusted with.
    ...(visitSharingPlanOn ? [{ name: t('sidebar.shared_visits', 'Shared with me'), icon: Share2, path: '/shared-visits', id: 'nav-shared-visits', badge: unseenShares }] : []),
    ...(doctorChatEnabled && hasFeature('doctor_patient_chat') ? [{ name: t('sidebar.patient_chat'), icon: MessageCircle, path: '/patientchat', id: 'nav-patient-chat' }] : []),
    ...feat('support_tickets', { name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' }),
    // Medicines visibility is a super-admin-controlled feature flag (Settings → Doctor).
    ...(hasFeature('medicines') ? [{ name: t('sidebar.medicines', 'Medicines'), icon: Pill, path: '/medicines', id: 'nav-medicines' }] : []),
    // Every doctor has a ledger (owner, 30 Sep 2026): a Free-plan doctor who
    // takes cash must still see what was collected. Only the page's ONLINE
    // parts follow the plan (online_payment) and the hospital's switch.
    { name: t('sidebar.payment'), icon: Landmark, path: '/payment', id: 'nav-payment' },
    ...feat('subscriptions', { name: t('sidebar.my_subscription', 'My Subscription'), icon: CreditCard, path: '/mySubscription', id: 'nav-my-subscription' }),
    ...(aiReportsPlanOn ? feat('ai_reports', { name: t('sidebar.ai_reports', 'AI Reports'), icon: BarChart3, path: '/aiReports', id: 'nav-ai-reports' }) : []),
    { name: t('sidebar.myprofile'), icon: UserRoundPen, path: '/doctorProfile', id: 'nav-doctor-profile' },
    { name: t('sidebar.doctor_settings', 'Settings'), icon: Settings2, path: '/doctorSettings', id: 'nav-doctor-settings' },
  ];

  // STAFF MENU.
  //
  // Built from what their sub-role actually grants, so two people with different
  // roles see different rails. `can()` resolves from /user-roles/me, which is
  // re-read on every load — a permission removed a minute ago is already gone
  // from the menu.
  //
  // Pharmacy, lab and radiology entries are deliberately absent: those
  // permissions exist and can be granted today, but their screens ship with
  // their modules. A menu item that 404s is worse than one that isn't there yet
  // — StaffDashboard lists the held permissions instead, marked "coming soon".
  const staffItems = [
    { name: t('sidebar.dashboard'), icon: LayoutDashboard, path: '/staffDashboard', id: 'nav-dashboard' },
    ...(can('appointments.view') && hasFeature('appointments') ? [{
      name: t('sidebar.appointment'),
      icon: Calendar,
      path: '/appointment',
      id: 'nav-appointment',
      hasDropdown: true,
      subItems: [
        { name: t('sidebar.calendar'), path: '/appointment', id: 'nav-appointment-calendar' },
        { name: t('sidebar.list'), path: '/appointmentList', id: 'nav-appointment-list' },
      ],
    }] : []),
    ...(can('patients.view') && hasFeature('patients')
      ? [{ name: t('sidebar.patient'), icon: Users, path: '/patient', id: 'nav-patient', activePaths: ['/patient', '/patientProfile'] }]
      : []),
    ...pharmacyNav,
    ...labNav,
    ...radNav,
    ...(can('support.view') && hasFeature('support_tickets')
      ? [{ name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' }]
      : []),
  ];

  // PATIENT MENU — still the flat list; the patient's two-level rail lands
  // with the Phase-3 patient web app.
  //
  // Ordered the way a visit actually goes: act (book), look back (what I
  // booked), the people (my doctors, then talking to them), money, account.
  // Chat used to sit between profile and payment, three rows away from the
  // doctors it is about. Same routes and same ids as before — only the order
  // moved, so nothing bookmarked or linked breaks.
  const patientItems = [
    { name: t('sidebar.dashboard'), icon: LayoutDashboard, path: '/patientDashboard', id: 'nav-dashboard' },
    // "Appointment" next to "My appointment" read as the same line twice; the
    // verb is what separates booking a new one from reviewing the old ones.
    { name: t('sidebar.appointment1', 'Book appointment'), icon: Calendar, path: '/patientAppointment', id: 'nav-patient-appointment' },
    { name: t('sidebar.myappointtment', 'My appointments'), icon: CalendarDays, path: '/myAppointment', id: 'nav-my-appointment' },
    { name: t('sidebar.my_doctor'), icon: Stethoscope, path: '/myDoctor', id: 'nav-my-doctor' },
    // Chat sits with the doctors it is about, not between profile and payment.
    // { name: t('sidebar.chat'), icon: MessageCircle, path: '/chat', id: 'nav-chat' },
    { name: t('sidebar.doctor_chat'), icon: MessageCircle, path: '/doctorchat', id: 'nav-doctor-chat' },
    { name: t('sidebar.payment'), icon: Landmark, path: '/payment', id: 'nav-payment' },
    { name: t('sidebar.support'), icon: HelpCircle, path: '/support', id: 'nav-support' },
    // Account last, as in every other role.
    { name: t('sidebar.myprofile'), icon: UserRoundPen, path: '/patientProfile', id: 'nav-patient-profile' },
  ];

  const handleNavClick = (e, targetPath) => {
    if (resnavigate) {
      e.preventDefault();
      // Remember where the user wanted to go so the dirty tab can auto-redirect
      // to it after the user confirms Discard.
      if (targetPath) {
        dispatch(setPendingNavigation({ kind: 'route', value: targetPath }));
      }
      dispatch(tryingnavigate(true));
      return;
    }
    if (window.innerWidth <= 1024) onClose();
  };

  // Fixed: Now supports all dropdown types including Reports
  const handleDropdownClick = (e, dropdownType, targetPath, dropdownId = null) => {
    if (resnavigate) {
      e.preventDefault();
      if (targetPath) {
        dispatch(setPendingNavigation({ kind: 'route', value: targetPath }));
      }
      dispatch(tryingnavigate(true));
      return;
    }

    if (window.innerWidth <= 1024) onClose();

    // Toggle based on type
    if (dropdownType === 'dashboard') {
      setDashboardDropdownOpen(prev => !prev);
    } else if (dropdownType === 'appointment') {
      setAppointmentDropdownOpen(prev => !prev);
    } else if (dropdownType === 'reports') {
      setReportsDropdownOpen(prev => !prev);
    } else if (dropdownType === 'master') {
      setMasterDropdownOpen(prev => !prev);
    } else if (dropdownType === 'patient') {
      setPatientDropdownOpen(prev => !prev);
    } else if (dropdownType === 'doctor') {
      setDoctorDropdownOpen(prev => !prev);
    } else if (dropdownType === 'subscription') {
      setSubscriptionDropdownOpen(prev => !prev);
    } else if (dropdownType === 'audit') {
      setAuditDropdownOpen(prev => !prev);
    } else if (dropdownId) {
      // Anything not in the legacy chain — Users, Pharmacy, and whatever is
      // added next — toggles by its own id and needs no case here.
      toggleDropdown(dropdownId);
    }
  };

  // After a dirty tab confirms Discard, `resnavigate` flips back to false while
  // `pendingNavigation` still holds the original destination. Auto-perform the
  // route navigation here so the user lands where they originally clicked.
  useEffect(() => {
    if (!resnavigate && pendingNavigation?.kind === 'route' && pendingNavigation.value) {
      navigate(pendingNavigation.value, pendingNavigation.state ? { state: pendingNavigation.state } : undefined);
      dispatch(clearPendingNavigation());
      if (window.innerWidth <= 1024 && typeof onClose === 'function') {
        onClose();
      }
    }
  }, [resnavigate, pendingNavigation, navigate, dispatch, onClose]);

  // Tooltip position state
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, visible: false, text: '' });
  const navRef = useRef(null);

  const handleTooltipShow = (e, text) => {
    if (!isOpen && window.innerWidth >= 1024) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 16,
        visible: true,
        text: text
      });
    }
  };

  const handleTooltipHide = () => {
    setTooltipPosition(prev => ({ ...prev, visible: false }));
  };

  // Update tooltip position on scroll
  useEffect(() => {
    if (!isOpen && window.innerWidth >= 1024 && tooltipPosition.visible && navRef.current) {
      const handleScroll = () => {
        setTooltipPosition(prev => ({ ...prev, visible: false }));
      };
      navRef.current.addEventListener('scroll', handleScroll);
      return () => {
        if (navRef.current) {
          navRef.current.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [tooltipPosition.visible, isOpen]);

  const renderNavItem = (item) => {
    const currentPath = location.pathname;
    const isActive = (item.activePaths ? item.activePaths.includes(currentPath) : currentPath === item.path) || item.subItems?.some(sub => sub.path === currentPath);
    const isSubItemActive = item.subItems?.some(sub => sub.path === currentPath);

    const isDashboardDropdown = item.id === 'nav-dashboard';
    const isAppointmentDropdown = item.id === 'nav-appointment';
    const isReportsDropdown = item.id === 'nav-reports';
    const isMasterDropdown = item.id === 'nav-master';
    const isPatientDropdown = item.id === 'nav-patient';
    const isDoctorDropdown = item.id === 'nav-doctor';
    const isSubscriptionDropdown = item.id === 'nav-subscription';
    const isAuditDropdown = item.id === 'nav-audit';

    // Legacy per-menu flags first, then the generic map for everything else.
    const dropdownOpen =
      Boolean(openDropdowns[item.id]) ||
      (isDashboardDropdown && dashboardDropdownOpen) ||
      (isAppointmentDropdown && appointmentDropdownOpen) ||
      (isReportsDropdown && reportsDropdownOpen) ||
      (isMasterDropdown && masterDropdownOpen) ||
      (isPatientDropdown && patientDropdownOpen) ||
      (isDoctorDropdown && doctorDropdownOpen) ||
      (isSubscriptionDropdown && subscriptionDropdownOpen) ||
      (isAuditDropdown && auditDropdownOpen);

    const dropdownType = isDashboardDropdown ? 'dashboard' : isAppointmentDropdown ? 'appointment' : isReportsDropdown ? 'reports' : isMasterDropdown ? 'master' : isPatientDropdown ? 'patient' : isDoctorDropdown ? 'doctor' : isSubscriptionDropdown ? 'subscription' : isAuditDropdown ? 'audit' : '';

    if (item.hasDropdown) {
      return (
        <div key={item.id} data-testid={`${item.id}-wrapper`} className="relative overflow-hidden">
          <NavLink
            to={item.path}
            data-testid={item.id}
            onClick={(e) => handleDropdownClick(e, dropdownType, item.path, item.id)}
            onMouseEnter={!isOpen && window.innerWidth >= 1024 ? (e) => handleTooltipShow(e, item.name) : undefined}
            onMouseLeave={!isOpen && window.innerWidth >= 1024 ? handleTooltipHide : undefined}
            className={`flex items-center gap-3 py-3 rounded-2xl font-medium group relative overflow-hidden w-full min-w-0
              ${(isActive || isSubItemActive) && (isOpen || window.innerWidth >= 1024)
                ? 'text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100 hover:text-[#4ABDBD]'
              } ${!isOpen ? 'justify-center px-0' : 'justify-start px-4'}`}
          >
            {/* Animated active background */}
            {(isActive || isSubItemActive) && (isOpen || window.innerWidth >= 1024) && (
              <motion.div
                layoutId="sidebar-active-bg"
                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-2xl"
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              />
            )}

            <div className={`relative z-10 flex items-center ${!isOpen ? 'justify-center' : 'gap-3 min-w-0 flex-1'}`}>
              {(isOpen || window.innerWidth >= 1024) && (
                <item.icon
                  data-testid={`nav-link-icon-${item.id}`}
                  className={`w-5 h-5 flex-shrink-0 transition-colors duration-300 ease-in-out ${(isActive || isSubItemActive) ? 'text-white' : 'text-gray-500 group-hover:text-[#4ABDBD]'}`}
                />
              )}

              <span className={`transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 max-w-0'} overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1 ${(isActive || isSubItemActive) ? 'text-white font-semibold' : 'group-hover:text-[#4ABDBD]'}`}>
                {item.name}
              </span>
            </div>
          </NavLink>

          {/* Submenu */}
          {isOpen && dropdownOpen && item.subItems && (
            <div data-testid={`${item.id}-submenu`} className="ml-8 mt-2 space-y-1 overflow-hidden">
              {item.subItems.map((subItem) => {
                const isSubActive = (subItem.activePaths ? subItem.activePaths.includes(currentPath) : currentPath === subItem.path);
                return (
                  <NavLink
                    key={subItem.id}
                    to={subItem.path}
                    data-testid={subItem.id}
                    onClick={(e) => handleNavClick(e, subItem.path)}
                    className={`relative block px-4 py-2 rounded-xl text-sm overflow-hidden text-ellipsis whitespace-nowrap ${isSubActive
                      ? 'text-[#4ABDBD] font-semibold'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#4ABDBD]'
                      }`}
                  >
                    {isSubActive && (
                      <motion.div
                        layoutId="sidebar-sub-active-bg"
                        className="absolute inset-0 bg-[#4ABDBD]/10 rounded-xl border-l-4 border-[#4ABDBD]"
                        initial={false}
                        animate={{ opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{subItem.name}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular item (no dropdown)
    const isRegularActive = (item.activePaths ? item.activePaths.includes(currentPath) : currentPath === item.path);

    return (
      <div key={item.id} data-testid={`${item.id}-wrapper`} className="relative overflow-hidden">
        <NavLink
          to={item.path}
          data-testid={item.id}
          onClick={(e) => handleNavClick(e, item.path)}
          onMouseEnter={!isOpen && window.innerWidth >= 1024 ? (e) => handleTooltipShow(e, item.name) : undefined}
          onMouseLeave={!isOpen && window.innerWidth >= 1024 ? handleTooltipHide : undefined}
          className={`flex items-center gap-3 py-3 rounded-2xl font-medium group relative overflow-hidden w-full min-w-0
            ${isRegularActive && (isOpen || window.innerWidth >= 1024)
              ? 'text-white shadow-lg'
              : 'text-gray-600 hover:bg-gray-100 hover:text-[#4ABDBD]'
            } ${!isOpen ? 'justify-center px-0' : 'justify-start px-4'}`}
        >
          {/* Animated active background */}
          {isRegularActive && (isOpen || window.innerWidth >= 1024) && (
            <motion.div
              layoutId="sidebar-active-bg"
              className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-2xl"
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            />
          )}

          <div className={`relative z-10 flex items-center ${!isOpen ? 'justify-center' : 'gap-3 min-w-0 flex-1'}`}>
            {(isOpen || window.innerWidth >= 1024) && (
              <item.icon
                data-testid={`nav-link-icon-${item.id}`}
                className={`w-5 h-5 flex-shrink-0 transition-colors duration-300 ease-in-out ${isRegularActive ? 'text-white' : 'text-gray-500 group-hover:text-[#4ABDBD]'}`}
              />
            )}
            <span className={`transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 max-w-0'} overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1 ${isRegularActive ? 'text-white font-semibold' : 'group-hover:text-[#4ABDBD]'}`}>
              {item.name}
            </span>
            {item.badge > 0 && isOpen && (
              <span data-testid={`${item.id}-badge`} className="relative z-10 ml-auto rounded-full bg-[#D7A05C] px-1.5 py-px text-[10px] font-bold text-white">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </div>
        </NavLink>
      </div>
    );
  };

  // ROLES ARE ENUMERATED, NEVER FALLEN THROUGH. An unrecognised role used to
  // land on patientItems — a pharmacist shown the patient rail, links they
  // cannot open and no sign anything was wrong. Only the literal 'patient'
  // role gets the patient menu now; a future role someone forgets to wire
  // gets an EMPTY rail, which is loud enough to be fixed the same day.
  const navItems = userRole === 'admin'
    ? adminItems
    : userRole === 'doctor'
      ? doctorItems
      : userRole === 'user'
        ? staffItems
        : userRole === 'patient'
          ? patientItems
          : [];

  // ---------------------------------------------------------------------
  // TWO-LEVEL SIDEBAR (services restructure) — tenant roles only.
  //
  // The six services multiplied the tenant menu past what one flat list can
  // hold, so admin / doctor / staff get an icon RAIL (one icon per group)
  // with a detail PANEL (that group's items in sections) — see
  // TwoLevelSidebar.jsx. Groups are built HERE from the same feat()/can()
  // gates as the flat items above, so entitlement logic stays in one file.
  // super_admin and the patient rail keep the classic sidebar (decision:
  // patient's version lands with the Phase-3 patient web app).
  // ---------------------------------------------------------------------
  const pruneGroups = (gs) => gs
    .map((g) => ({ ...g, sections: g.sections.filter((s) => s.items.length > 0) }))
    .filter((g) => g.sections.length > 0);

  // GROUPS BECOME THE COLUMN IN DECLARED ORDER (see buildFlatFromGroups
  // below). A group whose sections are all TITLED is promoted — each section
  // is its own collapsible heading — while a group of untitled sections (the
  // departments: Pharma, Lab, Radiology, Caretaker) becomes ONE heading named
  // by the group label. Feature and permission pruning still applies, so a
  // section or group with nothing visible for this tenant or this person's
  // sub-role disappears from the column.
  let twoLevelGroups = null;
  if (!isSuperAdmin && userRole === 'admin') {
    // TENANT ADMIN — the approved order, top to bottom:
    //   Dashboard · Appointments · Doctors · Doctor plans · Patients ·
    //   Pharma · Lab · Radiology · Caretaker · Reports · Billing ·
    //   Communication · Account
    //
    // The department groups sit BETWEEN the clinical headings and the org
    // headings, and promotion keeps a group's sections together in place, so
    // the admin's own headings are declared in two groups — 'clinical' before
    // the departments, 'org' after. That split is the only reason there are
    // two; a promoted group's label is never drawn, so the user sees thirteen
    // headings in one column, not two "groups".
    //
    // Nothing was added or removed and no gate moved. The old Doctor group
    // carried hasFeature('appointments') (== the medi_consult service is ON)
    // for everything under it; those items keep that gate individually now
    // that they are filed by what they are (dashboard, appointment, doctor,
    // patient, report) rather than by which service produced them.
    // pruneGroups drops a heading whose items all pruned away, so an all-off
    // section costs a heading with nothing under it exactly never.
    twoLevelGroups = pruneGroups([
      {
        id: 'clinical', label: t('sidebar.group_doctor', 'Doctor'), icon: Stethoscope, sections: [
          { id: 'dashboard', title: t('sidebar.dashboard'), items: [
            { id: 'nav-admin-dashboard', name: t('sidebar.my_dashboard'), path: '/adminDashboard' },
            ...(hasFeature('appointments') ? [{ id: 'nav-doctor-dashboard', name: t('sidebar.doctor_dashboard'), path: '/doctorDashboard' }] : []),
          ] },
          // Appointments belong to the Medi-Consult SERVICE: a pharmacy-only or
          // lab-only tenant must not see them.
          { id: 'appointments', title: t('sidebar.appointment'), items: [
            ...(hasFeature('appointments') ? [
              { id: 'nav-appointment-calendar', name: t('sidebar.calendar'), path: '/appointment' },
              { id: 'nav-appointment-list', name: t('sidebar.list'), path: '/appointmentList' },
            ] : []),
          ] },
          // Everything ABOUT doctors: the roster, getting one in (Send invite —
          // ClickToGo), approving one (Doctor review, open platform only), and
          // the doctor reports (report, onboarding status, daily count).
          //
          // DOCTOR REVIEW keeps its own gate, NOT the appointments one: that
          // gate says "medi_consult is on", isOpenPlatform says "this tenant IS
          // Mitra One Care", and the two are unrelated. Tying review to
          // medi_consult would mean a Super Admin unticking that service
          // silently removes the ONLY way to verify (Verified badge) or reject
          // (hidden from the platform-wide directory) a doctor. Same gate as
          // the route (OpenPlatformRoute)
          // and nothing else.
          { id: 'doctors', title: t('sidebar.section_doctors', 'Doctors'), items: [
            ...(hasFeature('appointments') ? feat('doctors', { id: 'nav-doctor', name: t('sidebar.doctor'), path: '/doctor', activePaths: ['/doctor', '/doctorProfile'] }) : []),
            ...(isOpenPlatform ? [{ id: 'nav-doctor-review', name: t('sidebar.doctor_review', 'Doctor review'), path: '/doctor-review' }] : []),
            ...(hasFeature('appointments') ? [
              { id: 'nav-reports-doctors', name: t('sidebar.reports_doctors'), path: '/doctors' },
              { id: 'nav-reports-doctor-status', name: t('sidebar.reports_doctor_status'), path: '/doctor-status' },
              { id: 'nav-reports-doctor-count', name: t('sidebar.reports_doctor_count', 'Doctor Count'), path: '/count/doctors' },
            ] : []),
          ] },
          // Doctor subscription billing — the hospital's own doctor billing
          // (packages, payments, enquiries) plus Sub-Items, the access-control
          // catalogue those packages draw from. Open platform (Tenant #1)
          // only; feat('subscriptions') is defaultOff everywhere else.
          ...feat('subscriptions', { id: 'doctor-plans', title: t('sidebar.section_doctor_plans', 'Doctor plans'), items: [
            { id: 'nav-subscription-packages', name: t('sidebar.packages', 'Packages'), path: '/subscription' },
            { id: 'nav-subscription-payments', name: t('sidebar.payments', 'Payments'), path: '/subscriptionPayments' },
            { id: 'nav-subscription-sub-items', name: t('sidebar.sub_items', 'Sub-Items'), path: '/sub-items' },
          ] }),
          // Patient records are only meaningful where a service actually
          // treats/consults them — right now that's Doctor (medi_consult).
          // A tenant bought purely for pharmacy/lab/radiology never opens a
          // clinical patient record, so this follows 'appointments' rather
          // than staying a permanent global-core item. The two patient
          // reports live with the list they report on.
          //
          // The SAME heading in the staff rail below — one screen must not
          // have two homes depending on who logged in.
          { id: 'patients', title: t('sidebar.section_patients', 'Patients'), items: [
            ...(hasFeature('appointments') ? [
              { id: 'nav-patient', name: t('sidebar.patient'), path: '/patient', activePaths: ['/patient', '/patientProfile'] },
              { id: 'nav-reports-patients', name: t('sidebar.reports_patients'), path: '/patients' },
              { id: 'nav-reports-patient-count', name: t('sidebar.reports_patient_count', 'Patient Count'), path: '/count/patients' },
            ] : []),
          ] },
        ],
      },
      // PHARMA / LAB / RADIOLOGY — one heading per service, unchanged inside.
      ...((hasFeature('pharmacy') && pharmacySubItems.length) ? [{ id: 'pharmacy', label: t('sidebar.group_pharma', 'Pharma'), icon: Pill, sections: [{ items: pharmacySubItems }] }] : []),
      ...((hasFeature('lab') && labSubItems.length) ? [{ id: 'lab', label: t('sidebar.group_lab', 'Lab'), icon: TestTube, sections: [{ items: labSubItems }] }] : []),
      ...((hasFeature('radiology') && radSubItems.length) ? [{ id: 'radiology', label: t('sidebar.group_radiology', 'Radiology'), icon: Scan, sections: [{ items: radSubItems }] }] : []),
      // CARETAKER — home visits (Phase 5, #6): the Care Taker service's board.
      ...feat('care_visits', {
        id: 'care', label: t('sidebar.group_caretaker', 'Caretaker'), icon: HeartHandshake, sections: [{ items: [
          { id: 'nav-care-visits', name: t('sidebar.care_visits', 'Home visits'), path: '/care/visits' },
        ] }],
      }),
      // AMBULANCE — the service is on hold platform-wide (HOLD_SERVICE_IDS
      // in tenantFeatures.js forces it off for every tenant, no exceptions),
      // so there is no tenant it is honest to show a placeholder to. Bring
      // this back gated on hasFeature('ambulance') once the service ships.
      // THE ORG SIDE — reports, money, people, account.
      {
        id: 'org', label: t('sidebar.group_admin', 'Admin'), icon: Building2, sections: [
          // Cross-service reports. The doctor and patient reports are filed
          // under Doctors / Patients above, with the lists they report on;
          // Service reports follows 'reports', AI Reports follows 'ai_reports'.
          { id: 'reports', title: t('sidebar.reports'), items: [
            // Per-service reports (Phase 4, #16): billing, orders, TAT, stock.
            ...(hasServiceReports ? feat('reports', { id: 'nav-reports-services', name: t('sidebar.reports_services', 'Service reports'), path: '/reports/services' }) : []),
            ...feat('ai_reports', { id: 'nav-ai-reports', name: t('sidebar.ai_reports', 'AI Reports'), path: '/aiReports' }),
          ] },
          { id: 'billing', title: t('sidebar.section_billing', 'Billing'), items: [
            // Unified billing (Phase 4, #16) — shown when any billable service is on.
            ...((hasFeature('lab') || hasFeature('radiology')) ? [{ id: 'nav-service-billing', name: t('sidebar.service_billing', 'Service billing'), path: '/billing/services' }] : []),
          ] },
          // Talking to people: inside the hospital (Team chat) and to whoever
          // raised a ticket (Support). Same heading, same two items, in the
          // doctor and staff rails too.
          { id: 'communication', title: t('sidebar.section_communication', 'Communication'), items: [
            ...feat('team_chat', { id: 'nav-team-chat', name: t('sidebar.team_chat', 'Team chat'), path: '/team-chat' }),
            ...feat('support_tickets', { id: 'nav-support', name: t('sidebar.support'), path: '/support' }),
          ] },
          // The hospital's own check sets (check sets, Round A): readings
          // every doctor of the hospital may use or copy. A consult feature
          // (it needs appointments) that the hospital must have bought.
          { id: 'clinical-setup', title: t('sidebar.section_clinical_setup', 'Clinical setup'), items: [
            ...((hasFeature('appointments') && hasFeature('check_sets')) ? [{ id: 'nav-check-sets', name: t('sidebar.check_sets', 'Check sets'), path: '/check-sets' }] : []),
            // Care programmes (Round B): the hospital's follow-up timelines.
            ...((hasFeature('appointments') && hasFeature('check_sets') && hasFeature('care_programmes')) ? [{ id: 'nav-programmes', name: t('sidebar.programmes', 'Programmes'), path: '/programmes' }] : []),
          ] },
          // Account last, in every role — muscle memory only works if the
          // bottom of the list never moves. The hospital's own EMPLOYEES and
          // the sub-roles that decide what each may do sit here with the
          // admin's own profile and the tenant settings: all of it is
          // "who runs this account", none of it is a service.
          { id: 'account', title: t('sidebar.section_account', 'Account'), items: [
            ...feat('user_management',
              { id: 'nav-users-all', name: t('sidebar.all_users', 'All users'), path: '/users' },
              { id: 'nav-user-roles', name: t('sidebar.user_roles', 'Roles'), path: '/user-roles' },
            ),
            // Send invite moved here from Doctors (owner request, Aug 2026):
            // inviting people into the tenant reads as account administration.
            ...feat('invitations', { id: 'nav-clicktogo', name: t('sidebar.send_invite', 'Send invite'), path: '/clicktogo' }),
            { id: 'nav-admin-profile', name: t('sidebar.myprofile'), path: '/adminProfile' },
            { id: 'nav-settings', name: t('sidebar.settings', 'Settings'), path: '/settings' },
          ] },
        ],
      },
    ]);
  } else if (userRole === 'doctor') {
    // ONE rail icon, not two. A doctor's whole day lives under the Doctor
    // icon — dashboard, consult work, reports, the org screens they share,
    // their practice's money, their account — in titled sections. Service
    // groups a doctor has nothing in simply don't appear.
    //
    // There used to be a second, Admin, icon carrying just Team chat and
    // Support. Both are HOSPITAL features, so a tenant with both off rendered
    // that icon EMPTY: a two-icon rail with nothing to choose between. They
    // are a Communication section here instead, which also lines the doctor's
    // sections up with the admin's — Overview, work, Reports, Communication,
    // …, Account — so both rails read the same way.
    //
    // Approved order: Dashboard · Consult · Communication · My practice ·
    // Reports · Account.
    twoLevelGroups = pruneGroups([
      { id: 'doctor', label: t('sidebar.group_doctor', 'Doctor'), icon: Stethoscope, sections: [
        { id: 'overview', solo: true, title: t('sidebar.section_overview', 'Overview'), items: [
          { id: 'nav-dashboard', name: t('sidebar.dashboard'), path: '/doctorDashboard' },
        ] },
        // The consult day: appointments and patients.
        { id: 'consult', title: t('sidebar.section_consult', 'Consult'), items: [
          { id: 'nav-appointment-calendar', name: t('sidebar.calendar'), path: '/appointment' },
          { id: 'nav-appointment-list', name: t('sidebar.list'), path: '/appointmentList' },
          { id: 'nav-my-patient', name: t('sidebar.my_patient'), path: '/myPatient', activePaths: ['/myPatient', '/patientProfile'] },
          // Visits shared with this doctor from other hospitals (Phase 4) —
          // consult work, so it sits with the patients it is about.
          ...(visitSharingPlanOn ? [{ id: 'nav-shared-visits', name: t('sidebar.shared_visits', 'Shared with me'), path: '/shared-visits', badge: unseenShares }] : []),
        ] },
        // Talking to people: patients (plan-gated chat), the hospital (Team
        // chat is the HOSPITAL's feature, not a doctor-plan control) and
        // whoever answers a ticket. With all three off the section prunes
        // away.
        { id: 'communication', title: t('sidebar.section_communication', 'Communication'), items: [
          ...((doctorChatEnabled && hasFeature('doctor_patient_chat')) ? [{ id: 'nav-patient-chat', name: t('sidebar.patient_chat'), path: '/patientchat' }] : []),
          ...feat('team_chat', { id: 'nav-team-chat', name: t('sidebar.team_chat', 'Team chat'), path: '/team-chat' }),
          ...feat('support_tickets', { id: 'nav-support', name: t('sidebar.support'), path: '/support' }),
        ] },
        // The practice itself: the medicine list this doctor prescribes from,
        // payment setup and the doctor's own plan.
        { id: 'my-practice', title: t('sidebar.section_my_practice', 'My practice'), items: [
          ...(hasFeature('medicines') ? [{ id: 'nav-medicines', name: t('sidebar.medicines', 'Medicines'), path: '/medicines' }] : []),
          // Ungated since 30 Sep 2026: the ledger is every doctor's; only its
          // online parts depend on the plan (see the flat list above).
          { id: 'nav-payment', name: t('sidebar.payment'), path: '/payment' },
          ...feat('subscriptions', { id: 'nav-my-subscription', name: t('sidebar.my_subscription', 'My Subscription'), path: '/mySubscription' }),
        ] },
        // Own section, same heading the admin's reports carry.
        { id: 'reports', title: t('sidebar.reports'), items: [
          ...(aiReportsPlanOn ? feat('ai_reports', { id: 'nav-ai-reports', name: t('sidebar.ai_reports', 'AI Reports'), path: '/aiReports' }) : []),
        ] },
        // The doctor's own account: profile and consultation settings.
        { id: 'account', title: t('sidebar.section_account', 'Account'), items: [
          { id: 'nav-doctor-profile', name: t('sidebar.myprofile'), path: '/doctorProfile' },
          { id: 'nav-doctor-settings', name: t('sidebar.doctor_settings', 'Settings'), path: '/doctorSettings' },
        ] },
      ] },
    ]);
  } else if (userRole === 'user') {
    // Same rail order; pruneGroups drops whatever this staff member's
    // sub-role grants nothing in, so a pharmacist sees Pharma + Admin only.
    twoLevelGroups = pruneGroups([
      // 1. APPOINTMENTS — the consult work this sub-role grants, under the
      // heading the tenant admin sees for the same two screens (it used to
      // say "Doctor", which named the service rather than the screens).
      // Patients used to live here too, while the tenant admin reached the
      // same screen from the Admin group; it has moved to Admin below so one
      // screen has one home whoever logs in.
      //
      // CONSEQUENCE, and it is the intended one: this group prunes to EMPTY
      // for a sub-role holding no appointment permission (a pharmacist, a
      // billing executive), whose column then simply starts at Pharma.
      { id: 'appointments', label: t('sidebar.appointment'), icon: Calendar, sections: [
        ...((can('appointments.view') && hasFeature('appointments')) ? [{ items: [
          { id: 'nav-appointment-calendar', name: t('sidebar.calendar'), path: '/appointment' },
          { id: 'nav-appointment-list', name: t('sidebar.list'), path: '/appointmentList' },
        ] }] : []),
      ] },
      // 2–4. PHARMA / LAB / RADIOLOGY — one heading per service.
      ...((hasFeature('pharmacy') && pharmacySubItems.length) ? [{ id: 'pharmacy', label: t('sidebar.group_pharma', 'Pharma'), icon: Pill, sections: [{ items: pharmacySubItems }] }] : []),
      ...((hasFeature('lab') && labSubItems.length) ? [{ id: 'lab', label: t('sidebar.group_lab', 'Lab'), icon: TestTube, sections: [{ items: labSubItems }] }] : []),
      ...((hasFeature('radiology') && radSubItems.length) ? [{ id: 'radiology', label: t('sidebar.group_radiology', 'Radiology'), icon: Scan, sections: [{ items: radSubItems }] }] : []),
      // 7. ADMIN — the staff member's own dashboard, then the shared screens
      // their sub-role grants, in the SAME heading order the tenant admin's
      // org side uses (patients, reports, billing, communication) so an
      // employee and an admin describing the menu to each other are
      // describing the same menu. Every item keeps its own can() +
      // hasFeature() pair; pruneGroups drops the sections that empty.
      { id: 'admin', label: t('sidebar.group_admin', 'Admin'), icon: Building2, sections: [
        { id: 'overview', solo: true, title: t('sidebar.section_overview', 'Overview'), items: [
          { id: 'nav-dashboard', name: t('sidebar.dashboard'), path: '/staffDashboard' },
        ] },
        // Moved out of the Doctor group — same home as the tenant admin's.
        { id: 'patients', title: t('sidebar.section_patients', 'Patients'), items: [
          ...((can('patients.view') && hasFeature('patients')) ? [{ id: 'nav-patient', name: t('sidebar.patient'), path: '/patient', activePaths: ['/patient', '/patientProfile'] }] : []),
        ] },
        { id: 'reports', title: t('sidebar.reports'), items: [
          ...((can('reports.view') && hasFeature('reports') && hasServiceReports) ? [{ id: 'nav-reports-services', name: t('sidebar.reports_services', 'Service reports'), path: '/reports/services' }] : []),
        ] },
        // The Billing Executive's worklist.
        { id: 'billing', title: t('sidebar.section_billing', 'Billing'), items: [
          ...((can('billing.invoice.create') && (hasFeature('lab') || hasFeature('radiology'))) ? [{ id: 'nav-service-billing', name: t('sidebar.service_billing', 'Service billing'), path: '/billing/services' }] : []),
        ] },
        { id: 'communication', title: t('sidebar.section_communication', 'Communication'), items: [
          ...((can('chat.team.view') && hasFeature('team_chat')) ? [{ id: 'nav-team-chat', name: t('sidebar.team_chat', 'Team chat'), path: '/team-chat' }] : []),
          ...((can('support.view') && hasFeature('support_tickets')) ? [{ id: 'nav-support', name: t('sidebar.support'), path: '/support' }] : []),
        ] },
        // Same heading and screen as the tenant admin's; staff only with
        // checks.manage (Manage hospital check sets) on their role.
        { id: 'clinical-setup', title: t('sidebar.section_clinical_setup', 'Clinical setup'), items: [
          ...((can('checks.manage') && hasFeature('appointments') && hasFeature('check_sets')) ? [{ id: 'nav-check-sets', name: t('sidebar.check_sets', 'Check sets'), path: '/check-sets' }] : []),
          ...((can('checks.manage') && hasFeature('appointments') && hasFeature('check_sets') && hasFeature('care_programmes')) ? [{ id: 'nav-programmes', name: t('sidebar.programmes', 'Programmes'), path: '/programmes' }] : []),
        ] },
        // Account last, as for every role: the staff member's own profile —
        // name, photo and "How you sign in" (owner, 1 Oct 2026). Needs no
        // permission; everyone may see and verify their own contacts.
        { id: 'account', title: t('sidebar.section_account', 'Account'), items: [
          { id: 'nav-staff-profile', name: t('sidebar.myprofile'), path: '/staffProfile' },
        ] },
      ] },
    ]);
  }

  // ---------------------------------------------------------------------
  // SINGLE-COLUMN COLLAPSIBLE MODEL (navigation redesign).
  //
  // The rail is gone. The SAME twoLevelGroups built above (every gate intact)
  // are flattened here into { solo, sections } for CollapsibleSidebar — a
  // presentation-only transform, no gate touched, and NOTHING reordered: the
  // column reads exactly in the order the groups and sections were declared.
  //   • A group whose sections all carry a title (or `solo`) is PROMOTED:
  //     each titled section becomes a top-level collapsible section, and a
  //     `solo` section's items render as plain rows above the sections (the
  //     doctor's and staff member's dashboard row).
  //   • Any other group (the departments) becomes ONE collapsible section
  //     named by the group label, its items flattened in order.
  // Section ids are the declared `id`s (stable data-testids
  // `sidebar-section-<id>` — namespaced so they never collide with page-level
  // `section-*` cards such as PndtFormF's — and the per-section collapse
  // state keys on them).
  // Icons are attached per item id from NAV_ICON.
  // ---------------------------------------------------------------------
  const withIcon = (item) => ({ ...item, icon: NAV_ICON[item.id] || item.icon });

  const buildFlatFromGroups = (groups) => {
    const soloItems = [];
    const flatSections = [];
    groups.forEach((g) => {
      const promoted = g.sections.every((s) => s.title || s.solo);
      if (!promoted) {
        const items = g.sections.flatMap((s) => s.items).map(withIcon);
        flatSections.push({ id: g.id, title: g.label, items });
        return;
      }
      g.sections.forEach((s, i) => {
        if (s.solo) {
          s.items.forEach((it) => soloItems.push(withIcon(it)));
        } else {
          flatSections.push({ id: s.id || `${g.id}-${i}`, title: s.title, items: s.items.map(withIcon) });
        }
      });
    });
    return { solo: soloItems, sections: flatSections };
  };

  let flatModel = null;
  if (twoLevelGroups && twoLevelGroups.length > 0) {
    flatModel = buildFlatFromGroups(twoLevelGroups);
  } else if (userRole === 'patient') {
    // The patient list is short enough to render as solo items only (a
    // Doctor-Library dropdown, when enabled, keeps its sub-links inline).
    flatModel = { solo: patientItems.map(withIcon), sections: [] };
  }

  if (flatModel) {
    const roleLabel = isSuperAdmin
      ? t('sidebar.super_admin', 'Super Admin')
      : userRole === 'user'
        ? (staffRoleName || t('sidebar.staff', 'Staff'))
        : userRole === 'admin'
          ? t('sidebar.tenant_admin', 'Tenant Admin')
          : userRole === 'doctor'
            ? t('sidebar.doctors', 'Doctor')
            : t('sidebar.patients', 'Patient');
    return (
      <CollapsibleSidebar
        innerRef={ref}
        brandLogo={logo}
        user={{ name: user.name, image: user.image, roleLabel }}
        showBadge={showBadge}
        solo={flatModel.solo}
        sections={flatModel.sections}
        isOpen={isOpen}
        onNavigate={handleNavClick}
        onProfile={() => handleProfileNavigation('profile')}
      />
    );
  }

  return (
    <div
      ref={ref}
      className={`fixed inset-y-0 left-0 z-50 bg-white transform transition-all duration-300 shadow-lg flex flex-col h-screen overflow-y-hidden overflow-x-hidden
        ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0 lg:-translate-x-0 lg:w-16 '} lg:static`}
    >
      {/* Header — brand heart + plain-teal wordmark; collapsed rail shows the
          short "MOC" mark since the full name has no room. */}
      <div className={`flex items-center py-3 ${isOpen ? 'px-4 gap-2' : 'flex-col justify-center px-1 gap-1'}`}>
        <img
          src={logo}
          alt="Mitra One Care"
          className={`object-contain flex-shrink-0 ${isOpen ? 'h-10 w-10' : 'h-9 w-9'}`}
        />
        {isOpen ? (
          <span className="text-xl font-bold leading-none text-[#4ABDBD] whitespace-nowrap">Mitra One Care</span>
        ) : (
          <span className="text-[9px] font-extrabold tracking-widest text-[#2D8A8A]">MOC</span>
        )}
      </div>

      {/* Profile Section */}
      {isOpen && (
        <div
          data-testid="sidebar-profile-section"
          className={`relative flex flex-col bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 rounded-2xl border border-[#4ABDBD]/20 mb-4 py-3 gap-2 mt-2 px-3 flex-shrink-0 mx-4`}
        >
          {showBadge && (
            <BadgeCheck className="absolute top-2 right-2 w-6 h-6 text-white" fill="#10B981" />
          )}
          <div className="flex items-center gap-3">
            {isLoading ? (
              <HeartLoader data-testid="profile-loading" />
            ) : (user.image && user.image.trim() !== '' && !imageError) ? (
              <img
                data-testid="profile-image"
                src={getFileURL(user.image)}
                alt={user.name}

                className="w-12 h-12 rounded-full object-cover border-2 border-[#4ABDBD]/20 shadow-md"
                onError={() => setImageError(true)}
              />
            ) : (
              <div
                data-testid="profile-initial"
                className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-full shadow-lg text-white font-bold text-xl border-2 border-white"
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            {isOpen && !isLoading && (
              <div data-testid="profile-info" className="flex flex-col flex-1">
                <span data-testid="profile-name" className="font-bold text-gray-800 text-sm">
                  {user.name}
                </span>
                <span
                  data-testid="profile-role"
                  className="text-[#3A9B9B] text-xs w-fit font-medium bg-white px-2 py-1 rounded-lg shadow"
                >
                  {/* super_admin is normalised to Role='admin'; show its real label. */}
                  {isSuperAdmin ? t('sidebar.super_admin', 'Super Admin') : userRole === 'user' ? (staffRoleName || t('sidebar.staff', 'Staff')) : t(`sidebar.${userRole}s`)}
                </span>
              </div>
            )}
          </div>

          {/* Profile Completion Progress */}
          {isOpen && !isLoading && profileCompletion < 100 && userRole !== 'doctor' && userRole !== 'admin' && (
            <div
              data-testid="sidebar-profile-completion"
              className="space-y-1.5 shadow bg-white rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleProfileNavigation('profile')}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700">{t('sidebar.profile_completion')}</span>
                <span className={`text-[11px] font-bold ${getPercentageTextColor(profileCompletion)}`}>{profileCompletion}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${getProgressColor(profileCompletion)} shadow-sm`}
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>
            </div>
          )}

          {/* Doctor Setup Flow - Only for Doctors */}
          {isOpen && !isLoading && userRole === 'doctor' && setupStatus && !(setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4) && (
            <div className="shadow bg-white rounded-lg p-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-700">{t('sidebar.setup_progress')}</span>
                {/* {setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4 && ( */}
                <span className="text-[11px] font-bold text-[#4ABDBD]">{setupStatus.point4 ? '100%' : setupStatus.point3 ? '75%' : setupStatus.point2 ? '50%' : setupStatus.point1 ? '25%' : '0%'}</span>
                {/* )} */}
              </div>

              {/* Flow Points - Hide when 100% complete */}
              {/* {!(setupStatus.point1 && setupStatus.point2 && setupStatus.point3 && setupStatus.point4) && ( */}
              <div className="relative pt-2">
                {/* Connecting Line */}
                <div className="absolute top-3.5 left-6 right-6 h-1 bg-gray-300">
                  <div
                    className={`h-full transition-all duration-500 ${setupStatus.point4 ? 'bg-[#4ABDBD]' :
                      setupStatus.point3 ? 'bg-[#4ABDBD]' :
                        setupStatus.point2 ? 'bg-[#4ABDBD]' :
                          setupStatus.point1 ? 'bg-[#4ABDBD]' : 'bg-gray-300'
                      }`}
                    style={{
                      width: setupStatus.point4 ? '100%' :
                        setupStatus.point3 ? '75%' :
                          setupStatus.point2 ? '50%' :
                            setupStatus.point1 ? '25%' : '0%'
                    }}
                  />
                </div>

                {/* Points Container */}
                <div className="relative flex items-center justify-between px-2">
                  {/* Point 1: Profile Complete */}
                  <div
                    data-testid="sidebar-setup-point-profile"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('profile')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point1
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point1 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">P</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point1 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>P</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_profile')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 2: Appointment Speciality & Type */}
                  <div
                    data-testid="sidebar-setup-point-specialities"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('specialities')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point2
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point2 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">S</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point2 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>S</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_speciality')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 3: Address */}
                  <div
                    data-testid="sidebar-setup-point-address"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('address')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point3
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point3 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">A</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point3 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>AD</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_address')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>

                  {/* Point 4: Availability */}
                  <div
                    data-testid="sidebar-setup-point-availability"
                    className="group flex flex-col items-center relative z-10 cursor-pointer gap-1"
                    onClick={() => handleProfileNavigation('availability')}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all ${setupStatus.point4
                      ? 'bg-[#4ABDBD] border-[#4ABDBD]'
                      : 'bg-white border-gray-300'
                      }`}>
                      {setupStatus.point4 ? (
                        <Check className="w-2.5 h-2.5 text-white" />
                      ) : (
                        <span className="text-[9px] font-bold text-gray-400">A</span>
                      )}
                    </div>
                    <span className={`text-[10px] font-medium ${setupStatus.point4 ? 'text-[#4ABDBD]' : 'text-gray-400'
                      }`}>AV</span>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block">
                      <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                        {t('sidebar.setup_point_availability')}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* )} */}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav ref={navRef} className={`flex-1 overflow-y-auto overflow-x-hidden min-h-0 ${isOpen ? 'ml-4 pr-4 pb-2' : 'px-2'} space-y-2`} style={{ position: 'relative' }}>
        {navItems.map(renderNavItem)}
      </nav>

      {/* Footer */}
      {isOpen && (
        <footer className="p-4 border-t border-gray-200 text-center text-[9px] text-gray-500">
          <p>{t('sidebar.footer_copyright', { year: new Date().getFullYear() })}</p>
          <p className="mt-1">
            <span>{t('sidebar.app_version')}</span>{' '}
            <span className="text-[#4ABDBD] font-semibold">{t('sidebar.powered_by')}</span>
          </p>
        </footer>
      )}

      {/* Fixed Tooltip via Portal */}
      {!isOpen && window.innerWidth >= 1024 && tooltipPosition.visible && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed p-3 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white text-sm rounded-xl pointer-events-none shadow-xl whitespace-nowrap z-[99999]"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
          }}
        >
          {tooltipPosition.text}
        </div>,
        document.body
      )}
    </div>
  );
});

export default Sidebar;